import React, { useState, useEffect, useRef } from 'react';
import GridLayout from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

// Reusable renderField function with Tailwind classes - VIEW ONLY VERSION
const renderField = (field, value, fieldId) => {
  const { type, label, mandatory, boldText, options } = field;
  const baseLabelStyle = "block text-sm font-medium text-gray-700";
  const boldStyle = boldText ? "font-bold" : "";
  const requiredIndicator = mandatory ? <span className="text-red-500 ml-1">*</span> : null;

  // View mode only - display read-only content
  switch (type) {
    case "text":
    case "input":
      return (
        <div className="mb-4 flex items-center gap-2" key={fieldId}>
          <label className={`${baseLabelStyle} ${boldStyle}`}>
            {`${label}:`}{requiredIndicator}
          </label>
          <div className="text-gray-900">{value || ""}</div>
        </div>
      );

    case "textarea":
      return (
        <div className="mb-4" key={fieldId}>
          <label className={`${baseLabelStyle} ${boldStyle} mb-1 block`}>
            {label}
            {requiredIndicator}
          </label>
          <div className="text-gray-900 whitespace-pre-wrap">
            {value || ""}
          </div>
        </div>
      );

    case "heading":
      return (
        <div className="w-full mb-4" key={fieldId}>
          <h2 className={`text-xl ${boldStyle}`}>
            {label}
            {requiredIndicator}
          </h2>
        </div>
      );

    case "date":
      return (
        <div className="mb-4 flex items-center gap-2" key={fieldId}>
          <label className={`${baseLabelStyle} ${boldStyle}`}>
            {`${label}:`}{requiredIndicator}
          </label>
          <div className="text-gray-900">{value || ""}</div>
        </div>
      );

    case "select":
      return (
        <div className="mb-4 flex items-center gap-2" key={fieldId}>
          <label className={`${baseLabelStyle} ${boldStyle}`}>
            {`${label}:`}{requiredIndicator}
          </label>
          <div className="text-gray-900">{value || ""}</div>
        </div>
      );

    case "textEditor":
      return (
        <div className="mb-4" key={fieldId}>
          <div
            className="bg-white p-1 form-view-text-editor"
            dangerouslySetInnerHTML={{ __html: field.value || "" }}
          />
        </div>
      );

    case "radio":
      return (
        <div className="mb-4" key={fieldId}>
          <label className={`${baseLabelStyle} ${boldStyle} mb-2 block`}>
            {`${label}:`}{requiredIndicator}
          </label>
          <div className="flex gap-1">
            {options?.map((opt, idx) => (
              <div key={idx} className="flex items-center">
                <div className={`w-4 h-4 rounded-full border-2 mr-2 flex items-center justify-center 
                  ${value === opt ? 'border-blue-500' : 'border-gray-300'}`}>
                  {value === opt && <div className="w-2 h-2 rounded-full bg-blue-500"></div>}
                </div>
                <span className={value === opt ? 'text-blue-600 font-medium' : 'text-gray-700'}>
                  {opt}
                </span>
              </div>
            ))}
          </div>
        </div>
      );

    case "checkbox":
      return (
        <div className="mb-4" key={fieldId}>
          <label className={`${baseLabelStyle} ${boldStyle} mb-2 block`}>
            {`${label}:`}{requiredIndicator}
          </label>
          <div className="flex gap-1">
            {options?.map((opt, idx) => {
              const isChecked = Array.isArray(value) ? value.includes(opt) : value === opt;
              return (
                <div key={idx} className="flex items-center">
                  <div className={`w-4 h-4 border-2 mr-2 flex items-center justify-center 
                    ${isChecked ? 'bg-blue-500 border-blue-500' : 'border-gray-300'}`}>
                    {isChecked && (
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                    )}
                  </div>
                  <span className={isChecked ? 'text-blue-600 font-medium' : 'text-gray-700'}>
                    {opt}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      );

    case "file":
      return (
        <div className="mb-8" key={fieldId}>
          <label className={`${baseLabelStyle} ${boldStyle} mb-1 block`}>
            {`${label}:`}{requiredIndicator}
          </label>
          <div>
            {value ? (
              <div className="flex items-center gap-2">
                {typeof value === 'string' ? (
                  <>
                    {/* Show image preview if it's an image file */}
                    {value.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                      <div className="relative w-1/4">
                        <img
                          src={`https://d2n5q7sk1gggpw.cloudfront.net/${value}`}
                          alt={value}
                          className="rounded border object-cover"
                        />
                      </div>
                    ) : null}
                    <div className="">
                      {/* <div className="text-sm text-gray-600">Uploaded file: {value}</div> */}
                      <div className="text-sm">
                        <a 
                          href={`https://d2n5q7sk1gggpw.cloudfront.net/${value}`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-500 hover:underline"
                        >
                          View File
                        </a>
                      </div>
                    </div>
                  </>
                ) : (
                  // For newly selected files in edit mode that haven't been uploaded yet
                  <div className="bg-gray-100 p-2 rounded">
                    <div className="text-sm text-gray-600">Selected file: {value.name}</div>
                    <div className="text-sm text-gray-600">Size: {(value.size / 1024).toFixed(2)} KB</div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-gray-500">No file selected</div>
            )}
          </div>
        </div>
      );

    case "html":
      return (
        <div
          key={fieldId}
          className="mb-4"
          dangerouslySetInnerHTML={{ __html: label || "" }}
        />
      );

    default:
      return null;
  }
};

const FormViewer = ({
  formData,
  formStructure,
  onClose
}) => {
  const [fieldValues, setFieldValues] = useState({});
  const containerRef = useRef(null);
  useEffect(() => {
    if (formData && formData.content) {
      setFieldValues(formData.content);
    }
  }, [formData]);

  if (!formStructure || !formData) {
    return <div className="text-center py-5">Form data not available</div>;
  }

  return (
    <div className="w-full h-full flex items-center justify-center p-4">
      <div className="bg-gray-100 p-4 rounded-lg w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="bg-white border rounded-lg overflow-hidden flex-1 flex flex-col">
          <div className="p-6 w-full flex-1 overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold text-gray-800">
                {formStructure.formName}
              </h3>
              <button
                onClick={onClose}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            </div>
            
            {/* Form content */}
            <div ref={containerRef} className="border rounded-lg p-4 overflow-y-auto flex-1">
              {/* {formStructure.customFields.map((group, groupIndex) => (
                <GridLayout
                  key={groupIndex}
                  className="layout my-2"
                  cols={12}
                  rowHeight={30}
                  margin={[10, 10]}
                      style={{ fontSize: "12px" }}
                  width={800}
                  layout={group.map((field) => ({
                    i: field._id,
                    x: field.layout?.x || 0,
                    y: field.layout?.y || 0,
                    w: Math.min(field.layout?.w || 12, 12),
                    h: field.layout?.h || 1,
                  }))}
                  isDraggable={false}
                  isResizable={false}
                  useCSSTransforms={true}
                >
                  {group.map((field) => (
                    <div key={field._id} className="grid-item">
                      <div className="h-full">
                        {renderField(
                          field, 
                          fieldValues[field._id], 
                          field._id
                        )}
                      </div>
                    </div>
                  ))}
                </GridLayout>
              ))} */}
              {formStructure.customFields.map((group, groupIndex) => (
                <GridLayout
                  key={groupIndex}
                  className="layout my-2"
                  cols={12}
                  rowHeight={30}
                  margin={[10, 10]}
                  width={800}
                  layout={group.map((field) => {
                    // Reduce height by 100px (approx 3-4 rows) for textEditor fields
                    const baseHeight = field.layout?.h || 1;
                    const adjustedHeight = field.type === "textEditor" 
                      ? Math.max(1, baseHeight - 2) // Reduce by about 3 rows (90px at rowHeight 30)
                      : baseHeight;
                    
                    return {
                      i: field._id,
                      x: field.layout?.x || 0,
                      y: field.layout?.y || 0,
                      w: Math.min(field.layout?.w || 12, 12),
                      h: adjustedHeight,
                    };
                  })}
                  isDraggable={false}
                  isResizable={false}
                  useCSSTransforms={true}
                >
                  {group.map((field) => (
                    <div key={field._id} className="grid-item">
                      <div className="h-full">
                        {renderField(
                          field, 
                          fieldValues[field._id], 
                          field._id
                        )}
                      </div>
                    </div>
                  ))}
                </GridLayout>
              ))}              
            </div>
            
            {/* Action buttons */}
            <div className="border-t pt-4 mt-4 flex justify-end gap-2">
              <button
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Back to Forms
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormViewer;